import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { FleetSection } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { serverMessage } from '../../../../shared/error/server-message'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../shared/menu/menu'
import { OrgPicker } from '../../../../shared/org-search/org-picker'
import { IconCancel, IconNotification, IconInvite, IconRefresh, IconView } from '../../../../shared/icon/icon'
import { listEvents, listOutbox, parkEvent, relayOutbox, replayEvent } from '../../../../api-new/state/notify'
import { EVENT_STATUSES, durationText, eventStatusLabel } from '../../../../shared/notify/words'
import { agoCell, eventStatusCell, monoCell, organizationCell } from './cells'
import { ReasonDialog } from './reason-dialog'
import { refreshGrid, useCursorGrid } from './use-cursor-grid'
import { useNotifyOpsAccess } from './access'
import { timelinePath } from './paths'

const ALL_ORGANIZATIONS = { value: '', label: 'Every organization' }

const ageCell = row => (row.data ? <span title={`${row.value} seconds`}>{durationText(row.value)}</span> : '')

// The outbox and the events (spec section 13.1; system only, SYS_NOTIFY).
// The outbox holds only rows the relay has not moved yet, so its depth is
// the relay's lag; a stuck row can be relayed now. The events are what the
// relay moved: each one's status, attempts and recipients, with replay (run
// it again; deliveries already made are not repeated) and park (stop
// retrying a poison event). Every action is audited. The grids' search
// finds a notification type.
export const OutboxTab = () => {
  const history = useHistory()
  const access = useNotifyOpsAccess()
  const [organizationId, setOrganizationId] = useState('')
  const [status, setStatus] = useState('')
  const [notice, setNotice] = useState(null)
  const outboxRef = useRef()
  const eventsRef = useRef()

  const outbox = useCursorGrid({
    key: `outbox:${organizationId}`,
    load: ({ limit, cursor, search }) => listOutbox({ limit, cursor, organizationId, type: search }),
    failed: "The outbox couldn't be loaded."
  })
  const events = useCursorGrid({
    key: `events:${organizationId}:${status}`,
    load: ({ limit, cursor, search }) => listEvents({ limit, cursor, organizationId, status, type: search }),
    failed: "The events couldn't be loaded."
  })

  const [relay, setRelay] = useState(null)
  const [relayBusy, setRelayBusy] = useState(false)
  const [relayError, relayFailure, setRelayError] = useErrorLine()
  const [eventAction, setEventAction] = useState(null) // { kind: 'replay' | 'park', row }
  const [eventBusy, setEventBusy] = useState(false)
  const [eventError, eventFailure, setEventError] = useErrorLine()

  const openTimeline = useCallback(row => row && row.id && history.push(timelinePath(row.id, true)), [history])

  const doRelay = async () => {
    setRelayBusy(true)
    setRelayError(null)
    try {
      await relayOutbox(relay.id)
      setNotice(`Outbox row ${relay.id} was relayed.`)
      setRelay(null)
      refreshGrid(outboxRef)
      refreshGrid(eventsRef)
    } catch (err) {
      setRelayError(serverMessage(err, "The row couldn't be relayed."), err)
    } finally {
      setRelayBusy(false)
    }
  }

  const doEvent = async reason => {
    setEventBusy(true)
    setEventError(null)
    try {
      if (eventAction.kind === 'replay') await replayEvent(eventAction.row.id, reason)
      else await parkEvent(eventAction.row.id, reason)
      setNotice(eventAction.kind === 'replay' ? `Event ${eventAction.row.id} is being processed again.` : `Event ${eventAction.row.id} is parked.`)
      setEventAction(null)
      refreshGrid(eventsRef)
    } catch (err) {
      setEventError(serverMessage(err, eventAction.kind === 'replay' ? "The event couldn't be replayed." : "The event couldn't be parked."), err)
    } finally {
      setEventBusy(false)
    }
  }

  const outboxMenu = useMemo(
    () => r =>
      <Menu small>
        <MenuItem Icon={IconView} text="timeline" pin show onClick={() => openTimeline(r)} />
        <MenuItem Icon={IconInvite} text="relay now" pin show={access.canUpdate} onClick={() => { setRelayError(null); setRelay(r) }} />
      </Menu>,
    [access.canUpdate, openTimeline, setRelayError]
  )
  const eventMenu = useMemo(
    () => r =>
      <Menu small>
        <MenuItem Icon={IconView} text="timeline" pin show onClick={() => openTimeline(r)} />
        <MenuItem Icon={IconRefresh} text="replay" pin show={access.canUpdate} onClick={() => { setEventError(null); setEventAction({ kind: 'replay', row: r }) }} />
        <MenuItem Icon={IconCancel} text="park" pin show={access.canUpdate && r.status !== 'parked'} onClick={() => { setEventError(null); setEventAction({ kind: 'park', row: r }) }} />
      </Menu>,
    [access.canUpdate, openTimeline, setEventError]
  )

  return (
    <>
      {notice && <p className="text-success small" role="status">{notice}</p>}
      <div className="nf-toolbar">
        <span className="nf-org">
          <OrgPicker id="nf-outbox-org" label="Organization" value={organizationId} onChange={setOrganizationId} allOption={ALL_ORGANIZATIONS} small />
        </span>
      </div>

      <FleetSection id="nf-outbox" title="Outbox" lede="Notifications raised and not yet relayed, oldest waiting longest. Normally empty within seconds; rows here for minutes mean the relay is behind or down. Search by type.">
        {outbox.error && <p className="text-danger small" role="alert">{outbox.error}</p>}
        {outbox.error && <ErrorReference error={outbox.failure} className="mb-2" />}
        <FleetGrid title="Outbox" tableRef={outboxRef} exportName="notify-outbox" compact flush>
          <TableAgInfinite key={`outbox:${organizationId}`} ref={outboxRef} queryKey="outbox" onGetRows={outbox.getRows} onRowClicked={openTimeline}
            emptyState={{ Icon: IconNotification, title: 'Nothing waiting', message: 'Every raised notification has been relayed.' }}>
            <ColumnFormat field="createdAt" header="Raised" render={agoCell} sortable={false} />
            <ColumnFormat field="ageSeconds" header="Waiting" render={ageCell} sortable={false} fit="content" />
            <ColumnText field="type" header="Type" sortable={false} />
            <ColumnFormat field="organizationId" header="Organization" render={organizationCell} sortable={false} />
            <ColumnFormat field="aggregateId" header="About" render={r => (r.data ? <span className="sw-mono small">{`${r.data.aggregateType || ''}:${r.value || ''}`}</span> : '')} sortable={false} />
            <ColumnFormat field="requestId" header="Request" render={monoCell} sortable={false} />
            <ColumnMenu menu={outboxMenu} />
          </TableAgInfinite>
        </FleetGrid>
      </FleetSection>

      <FleetSection id="nf-events" title="Events"
        lede="What the relay moved, newest relayed first: whether each was processed, how often it was tried and how many people it reached. Replay runs one again (deliveries already made are not repeated); park stops a poison event. Search by type."
        actions={
          <select className="form-select form-select-sm" aria-label="Event status" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">Every status</option>
            {EVENT_STATUSES.map(s => <option key={s} value={s}>{eventStatusLabel(s)}</option>)}
          </select>}>
        {events.error && <p className="text-danger small" role="alert">{events.error}</p>}
        {events.error && <ErrorReference error={events.failure} className="mb-2" />}
        <FleetGrid title="Events" tableRef={eventsRef} exportName="notify-events" compact flush>
          <TableAgInfinite key={`events:${organizationId}:${status}`} ref={eventsRef} queryKey="events" onGetRows={events.getRows} onRowClicked={openTimeline}
            emptyState={{ Icon: IconNotification, title: 'No events', message: 'No events match these filters.' }}>
            {/* notify pages the events by relayed_at, newest first */}
            <ColumnFormat field="relayedAt" header="Relayed" render={agoCell} sortable={false} />
            <ColumnFormat field="createdAt" header="Raised" render={agoCell} sortable={false} />
            <ColumnText field="type" header="Type" sortable={false} />
            <ColumnFormat field="organizationId" header="Organization" render={organizationCell} sortable={false} />
            <ColumnFormat field="status" header="Status" render={eventStatusCell} sortable={false} fit="content" />
            <ColumnFormat field="attempts" header="Attempts" render={monoCell} sortable={false} fit="content" />
            <ColumnFormat field="recipients" header="Recipients" render={monoCell} sortable={false} fit="content" />
            <ColumnFormat field="replayCount" header="Replays" render={monoCell} sortable={false} fit="content" />
            <ColumnText field="failedReason" header="Why" sortable={false} />
            <ColumnMenu menu={eventMenu} />
          </TableAgInfinite>
        </FleetGrid>
      </FleetSection>

      <ConfirmDialog
        open={Boolean(relay)}
        title="Relay this row now?"
        body={relay ? `Outbox row ${relay.id} (${relay.type}) is moved into the events now, as the relay would. This is recorded.` : ''}
        confirmText="Relay now"
        busy={relayBusy}
        error={relayError}
        failure={relayFailure}
        onConfirm={doRelay}
        onCancel={() => setRelay(null)}
      />
      <ReasonDialog
        open={Boolean(eventAction)}
        title={eventAction && eventAction.kind === 'replay' ? 'Replay this event?' : 'Park this event?'}
        body={eventAction && eventAction.kind === 'replay'
          ? `Event ${eventAction.row.id} (${eventAction.row.type}) is processed again from the start. Notifications and deliveries already made are not repeated. This is recorded.`
          : eventAction ? `Event ${eventAction.row.id} (${eventAction.row.type}) stops being retried. This is recorded.` : ''}
        confirmText={eventAction && eventAction.kind === 'replay' ? 'Replay' : 'Park'}
        danger={Boolean(eventAction && eventAction.kind === 'park')}
        busy={eventBusy}
        error={eventError}
        failure={eventFailure}
        onConfirm={doEvent}
        onCancel={() => setEventAction(null)}
      />
    </>
  )
}
